import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Home } from 'lucide-react';
import { QRScanner } from '../components/QRScanner';
import { getUserCurrentEventId, getUserRole, submitPenalty, type PenaltySubmissionData } from '../lib/api';
import type { Location } from '../lib/supabase';

export function Holdtime() {
  return (
    <div className="pen-screen font-brutal flex flex-col min-h-screen">
      <HoldtimeContent />
    </div>
  );
}

function HoldtimeContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [raceNumber, setRaceNumber] = useState('');
  const [bonusTime, setBonusTime] = useState('');
  const [holdTimeReason, setHoldTimeReason] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(() => {
    return sessionStorage.getItem('currentLocation') || '';
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [athleteName, setAthleteName] = useState<string | null>(null);
  const [currentEventId, setCurrentEventId] = useState<number | null>(null);
  const [raceoffroleid, setRaceoffroleid] = useState<number | null>(null);
  const [judgestation, setJudgestation] = useState<number | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleLocationChange = (value: string) => {
    setSelectedLocation(value);
    sessionStorage.setItem('currentLocation', value);
  };

  useEffect(() => {
    // Clear legacy HOLD/RELEASE state if present
    localStorage.removeItem('activeHolds');

    const loadData = async () => {
      setIsLoading(true);

      const { data: locationsData, error: locationsError } = await supabase
        .from('location')
        .select('locationid, locationname, penaltytime')
        .order('locationid');

      if (locationsData && !locationsError) {
        setLocations(locationsData);

        const savedLocation = sessionStorage.getItem('currentLocation');
        if (savedLocation && locationsData.some((loc) => loc.locationid.toString() === savedLocation)) {
          setSelectedLocation(savedLocation);
        } else {
          sessionStorage.removeItem('currentLocation');
          if (locationsData.length > 0) {
            const firstLocationId = locationsData[0].locationid.toString();
            setSelectedLocation(firstLocationId);
            sessionStorage.setItem('currentLocation', firstLocationId);
          }
        }
      }

      if (user?.email) {
        const eventResponse = await getUserCurrentEventId(user.email);
        if (eventResponse.success && eventResponse.data) {
          setCurrentEventId(eventResponse.data.currenteventid);
        } else {
          setCurrentEventId(null);
        }

        const roleResponse = await getUserRole(user.email);
        if (roleResponse.success && roleResponse.data) {
          setRaceoffroleid(roleResponse.data.roleid);
          setJudgestation(roleResponse.data.judgestation);
        } else {
          setRaceoffroleid(null);
          setJudgestation(null);
        }
      }

      setIsLoading(false);
    };

    loadData();
  }, [user?.email]);

  useEffect(() => {
    if (raceoffroleid === 4 && judgestation !== null && locations.length > 0) {
      const judgeStationLocation = locations.find((loc) => loc.locationid === judgestation);
      if (judgeStationLocation) {
        const judgeStationLocationId = judgeStationLocation.locationid.toString();
        setSelectedLocation(judgeStationLocationId);
        sessionStorage.setItem('currentLocation', judgeStationLocationId);
      }
    }
  }, [raceoffroleid, judgestation, locations]);

  const handleQRScan = (result: string) => {
    try {
      const url = new URL(result);
      const raceNumberParam = url.searchParams.get('raceNumber');
      const athleteNameParam = url.searchParams.get('athleteName');

      if (raceNumberParam) {
        setRaceNumber(raceNumberParam);
      } else if (/^[A-Za-z]{5}\d{2}$/.test(result)) {
        setRaceNumber(result);
      } else {
        alert(`QR Code scanned but no valid race number found: ${result}`);
      }

      setAthleteName(athleteNameParam || null);
    } catch {
      if (/^[A-Za-z]{5}\d{2}$/.test(result)) {
        setRaceNumber(result);
      } else {
        alert(`QR Code scanned: ${result}`);
      }
      setAthleteName(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedLocation || !raceNumber || !bonusTime || !holdTimeReason || !user?.email) {
      setSubmitMessage(
        'Please fill in all required fields. Make sure you are logged in with a valid email.'
      );
      return;
    }

    const enteredSeconds = parseInt(bonusTime, 10);
    if (isNaN(enteredSeconds) || enteredSeconds <= 0) {
      setSubmitMessage('Error: Enter a valid bonus time in seconds (positive number).');
      return;
    }

    // Bonus time is stored as negative (credit), not a penalty
    const bonusSeconds = -Math.abs(enteredSeconds);

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const submissionData: PenaltySubmissionData = {
        locationId: parseInt(selectedLocation, 10),
        raceNumber: raceNumber.trim(),
        penaltyId: 4, // Hold / bonus time
        penaltyTime: bonusSeconds,
        judgeEmail: user.email,
        numPenalties: 1,
        photoUrl: undefined,
        eventid: currentEventId || 7,
        raceoffroleid: raceoffroleid || 4,
        notes: `Bonus time: ${holdTimeReason}`,
      };

      const result = await submitPenalty(submissionData);

      if (result.success) {
        setSubmitMessage(`Bonus time of ${enteredSeconds}s recorded for #${raceNumber}`);
        setRaceNumber('');
        setBonusTime('');
        setHoldTimeReason('');
        setAthleteName(null);
        setTimeout(() => setSubmitMessage(''), 2000);
      } else {
        setSubmitMessage(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error submitting bonus time:', error);
      setSubmitMessage('Error: Failed to record bonus time. Please try again.');
    }

    setIsSubmitting(false);
  };

  const handleClear = () => {
    setRaceNumber('');
    setBonusTime('');
    setHoldTimeReason('');
    setSubmitMessage('');
    setAthleteName(null);
  };

  return (
    <>
      <Header
        locations={locations}
        selectedLocation={selectedLocation}
        onLocationChange={handleLocationChange}
        isLoading={isLoading}
      />
      <div className="px-4 py-4 flex flex-col gap-3 max-w-lg mx-auto w-full flex-1 font-brutal">
        <QRScanner
          onScan={handleQRScan}
          stationName={
            locations.find((loc) => loc.locationid.toString() === selectedLocation)?.locationname ||
            undefined
          }
        />

        {isLoading && (
          <div className="text-center text-sw-muted text-xs uppercase tracking-wide font-extrabold font-brutal">
            Loading...
          </div>
        )}

        {athleteName && (
          <div className="bg-[#0e2a16] border-2 border-[rgba(48,176,53,0.35)] text-[#c7f0cb] px-3 py-2.5 text-[13px] text-center font-brutal">
            Athlete: <span className="font-extrabold">{athleteName}</span>
          </div>
        )}

        <form className="flex flex-col gap-3 flex-1" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="raceNumber" className="pen-label">
              Race Number <span className="text-sw-red">*</span>
            </label>
            <input
              id="raceNumber"
              placeholder="Enter Race Number"
              value={raceNumber}
              onChange={(e) => setRaceNumber(e.target.value)}
              className="pen-input !rounded-none font-brutal"
              required
            />
          </div>

          <div>
            <label htmlFor="bonusTime" className="pen-label">
              Bonus Time (seconds) <span className="text-sw-red">*</span>
            </label>
            <input
              id="bonusTime"
              type="number"
              placeholder="Enter bonus time in seconds"
              value={bonusTime}
              onChange={(e) => setBonusTime(e.target.value)}
              className="pen-input !rounded-none font-brutal [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              required
              min={1}
            />
          </div>

          <div>
            <label htmlFor="holdTimeReason" className="pen-label">
              Reason <span className="text-sw-red">*</span>
            </label>
            <input
              id="holdTimeReason"
              placeholder="e.g., Equipment delay, Station backup, etc."
              value={holdTimeReason}
              onChange={(e) => setHoldTimeReason(e.target.value)}
              className="pen-input !rounded-none font-brutal"
              required
            />
          </div>

          {submitMessage && (
            <div
              className={`${
                submitMessage.includes('Error') ? 'pen-error' : 'pen-success'
              } !rounded-none font-brutal`}
            >
              {submitMessage}
            </div>
          )}

          <div className="flex gap-2.5 my-5">
            <button
              type="submit"
              className="pen-btn flex-1 !rounded-none !text-[15px] font-brutal font-black bg-sw-green text-white border-sw-green hover:bg-black hover:text-sw-green"
              disabled={isSubmitting || isLoading || !selectedLocation}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="pen-btn-ghost flex-1 !rounded-none !text-[15px] font-brutal font-black !border-2 !border-sw-border !bg-transparent hover:!bg-white hover:!text-black"
              disabled={isSubmitting}
            >
              Clear
            </button>
          </div>

          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="pen-btn-ghost w-full mt-auto !rounded-none !text-[15px] font-brutal font-black !border-2 !border-sw-border"
          >
            GO BACK <Home className="w-4 h-4" />
          </button>
        </form>
      </div>
    </>
  );
}
